import React, { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { Button, Tooltip } from '@mui/material';
import KeyOffIcon from '@mui/icons-material/KeyOff';
import { useUnitKeyManagement } from '../../../services/api/useUnitKeyManagement';
import ResetKeyModal from './ResetKeyModal';

interface Props {
  canResetKey: boolean;
  disabledReason?: string;
}

const ResetUnitKeyButton: React.FC<Props> = ({ canResetKey, disabledReason }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext();
  const { revokeKey } = useUnitKeyManagement();

  const handleResetKeyClick = () => {
    if (!record?.hostname) {
      notify('No unit record found', { type: 'error' });
      return;
    }
    setModalOpen(true);
  };

  const handleConfirmReset = async (reason: string) => {
    if (!record?.hostname) {
      notify('No unit record found', { type: 'error' });
      throw new Error('No unit record found');
    }

    try {
      await revokeKey(record.hostname, reason);
      notify('SSH key revoked successfully', { type: 'success' });
      refresh();
    } catch (err) {
      notify('Failed to revoke SSH key', { type: 'error' });
      throw err;
    }
  };

  return (
    <>
      <Tooltip title={canResetKey ? 'Reset SSH Key' : disabledReason || ''}>
        <span>
          <Button
            variant="contained"
            color="warning"
            disabled={!canResetKey}
            onClick={handleResetKeyClick}
            startIcon={<KeyOffIcon />}
            size="small"
          >
            Reset SSH Key
          </Button>
        </span>
      </Tooltip>

      {record && (
        <ResetKeyModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirmReset}
          unitSerial={record.serial || 'Unknown'}
          unitHostname={record.hostname || 'Unknown'}
        />
      )}
    </>
  );
};

export default ResetUnitKeyButton;
